import React, { FormEvent, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import Button from '../components/button';
import { Grid } from '@mui/material';
import translations from './userprofile.i18n';
import * as css from './userprofile.module.scss';
import Icon, { IconType } from '../components/icon';
import classnames from 'classnames';
import { Textarea } from '../components/input';
import { TextInput } from '../components/input/textInput';
import validateEmail from '../util/validateEmail';
import validatePhoneNo, {
  phoneNoRegexPatternNotRequired,
} from '../util/validatePhoneNo';

export interface Bruker {
  id: string;
  fornavn: string;
  etternavn: string;
  epost: string;
  telefonnummer: string;
}

export interface ProfilData {
  bruker: Bruker;
  studiestedNavn: string;
  omMeg?: string;
  bildeUrl?: string;
  kanRedigere?: boolean;
}

interface UserProfileProps {
  brukerprofil: ProfilData;
  saveHandler?: (omMeg: string, epost: string, telefonnummer: string) => void;
  editableFields?: string[];
}

const getInitials = function (fornavn: string, etternavn: string) {
  let initials = '';
  for (const name of `${fornavn} ${etternavn}`.split(/\s+/)) {
    initials += name[0];
  }
  return initials;
};

const StudiestedUserprofile = React.memo<UserProfileProps>(
  ({ brukerprofil, saveHandler, editableFields = [] }) => {
    const [isEditMode, setIsEditMode] = useState(false);
    const { bruker, studiestedNavn, bildeUrl, kanRedigere } = brukerprofil;
    const { fornavn, etternavn } = bruker;
    const initials = getInitials(bruker.fornavn, bruker.etternavn);

    const [omMeg, setOmMeg] = useState(brukerprofil.omMeg || '');
    const [epost, setEpost] = useState(bruker.epost || '');
    const [telefonnummer, setTelefonnummer] = useState(
      bruker.telefonnummer || '',
    );
    const [validInput, setValidInput] = useState(true);

    useEffect(
      () =>
        setValidInput(
          validateEmail(epost) &&
            (!telefonnummer || validatePhoneNo(telefonnummer)),
        ),
      [epost, telefonnummer],
    );

    const saveProfile = (event: FormEvent) => {
      event.preventDefault();
      saveHandler && saveHandler(omMeg, epost, telefonnummer);
      setIsEditMode(false);
    };

    const cancelEdit = () => {
      setOmMeg(brukerprofil.omMeg || '');
      setIsEditMode(false);
    };

    const fieldIsEditable = (fieldName: string) =>
      editableFields.includes(fieldName);

    const { formatMessage } = useIntl();
    const labels = {
      telefon: formatMessage({ id: translations.telefon.id }),
      epost: formatMessage({ id: translations.epost.id }),
    };

    const contactDisplay = (
      <>
        <Grid item={true} xs={3} className="telefonnummer">
          <p>
            <span className={css.label}>{labels.telefon}</span>
            {` ${bruker.telefonnummer || ''}`}
          </p>
        </Grid>
        <Grid item={true} xs={5} className="epost">
          <p>
            <span className={css.label}>{labels.epost}</span>
            {` ${bruker.epost || ''}`}
          </p>
        </Grid>
      </>
    );

    const formId = 'contact-info-form';

    const contactEdit = (
      <Grid item xs={8}>
        <form id={formId} action={''} method={'post'} onSubmit={saveProfile}>
          <Grid container spacing={1}>
            <Grid item xs={5}>
              <TextInput
                htmlId={'telefonnummer'}
                label={labels.telefon}
                value={telefonnummer}
                onChange={setTelefonnummer}
                autoComplete={'off'}
                type={'tel'}
                pattern={phoneNoRegexPatternNotRequired}
              />
            </Grid>
            <Grid item xs={7}>
              <TextInput
                htmlId={'epost'}
                required
                label={labels.epost}
                value={epost}
                onChange={setEpost}
                autoComplete={'off'}
                type={'email'}
              />
            </Grid>
          </Grid>
        </form>
      </Grid>
    );

    return (
      <div>
        <Grid
          container={true}
          className={classnames(css.container, isEditMode ? css.editMode : '')}
        >
          {/* Action buttons */}
          {saveHandler && (
            <Grid item={true} xs={12}>
              <ActionButtons
                cancel={cancelEdit}
                kanRedigere={!!kanRedigere}
                kanLagre={validInput}
                isEditMode={isEditMode}
                setIsEditMode={setIsEditMode}
                formId={formId}
              />
            </Grid>
          )}

          {/* Left side profile image */}
          <Grid item={true} xs={3}>
            <ReadBilde bildeUrl={bildeUrl} initials={initials} />
          </Grid>

          {/* Right side profile info */}
          <Grid item={true} xs={8}>
            <Grid container={true}>
              <Grid item={true} xs={12}>
                <p className={css.name}>{`${fornavn} ${etternavn}`}</p>
              </Grid>
              <Grid item={true} xs={12}>
                <Grid container={true} spacing={1}>
                  <Grid item={true} xs={4}>
                    <div className={css.iconLine}>
                      <Icon type={IconType.AccountBalance} />
                      <p>{` ${studiestedNavn}`}</p>
                    </div>
                  </Grid>
                  {isEditMode ? contactEdit : contactDisplay}
                </Grid>
              </Grid>
              <Grid item={true} xs={12}>
                <OmMeg
                  initialOmMeg={brukerprofil.omMeg}
                  omMeg={omMeg}
                  setOmMeg={setOmMeg}
                  isEditMode={isEditMode}
                  fieldIsEditable={fieldIsEditable}
                  formId={formId}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </div>
    );
  },
);

const ActionButtons: React.FC<
  React.PropsWithChildren<{
    cancel: () => void;
    kanRedigere: boolean;
    kanLagre: boolean;
    isEditMode: boolean;
    setIsEditMode: (isEditMode: boolean) => void;
    formId: string;
  }>
> = ({ cancel, kanRedigere, kanLagre, isEditMode, setIsEditMode, formId }) => {
  const { formatMessage } = useIntl();
  const labels = {
    endre: formatMessage({ id: translations.endre.id }),
    avbryt: formatMessage({ id: translations.avbryt.id }),
    lagre: formatMessage({ id: translations.lagre.id }),
  };

  return (
    <div className={css.buttonContainer}>
      {!isEditMode && kanRedigere && (
        <Button.Secondary
          onClick={() => setIsEditMode(true)}
          className="changeButton"
        >
          {labels.endre}
        </Button.Secondary>
      )}
      {isEditMode && (
        <>
          <Button.Tertiary onClick={cancel}>{labels.avbryt}</Button.Tertiary>
          <Button.Secondary
            disabled={!kanLagre}
            className="saveButton"
            form={formId}
          >
            {labels.lagre}
          </Button.Secondary>
        </>
      )}
    </div>
  );
};

const ReadBilde: React.FC<
  React.PropsWithChildren<{
    bildeUrl: string | undefined;
    initials: string;
  }>
> = ({ bildeUrl, initials }) => {
  const { formatMessage } = useIntl();
  return (
    <>
      {bildeUrl && (
        <img
          aria-hidden="true"
          src={bildeUrl}
          className={css.profilePicture}
          alt={formatMessage({ id: translations.profileImgAltText.id })}
        />
      )}
      {!bildeUrl && (
        <div
          aria-hidden="true"
          className={classnames(
            css.profilePictureReplacement,
            'profilePictureReplacement',
          )}
        >
          <p>{initials}</p>
        </div>
      )}
    </>
  );
};

const OmMeg: React.FC<
  React.PropsWithChildren<{
    omMeg: string;
    setOmMeg: (tlf: string) => void;
    initialOmMeg: string | undefined;
    isEditMode: boolean;
    fieldIsEditable: (fieldName: string) => boolean;
    formId: string;
  }>
> = ({
  omMeg,
  setOmMeg,
  initialOmMeg,
  isEditMode,
  fieldIsEditable,
  formId,
}) => {
  const { formatMessage } = useIntl();
  const labels = {
    omMeg: formatMessage({ id: translations.omMeg.id }),
  };

  return (
    <>
      <Grid container={true}>
        <Grid item={true} xs={2}>
          <label className={css.aboutMeHeader} htmlFor={'om-meg'}>
            {labels.omMeg}
          </label>
        </Grid>
        <Grid item={true} xs={10}>
          <hr />
        </Grid>
        <Grid
          item={true}
          xs={12}
          className={classnames(css.aboutMeText, 'omMeg')}
        >
          {(!isEditMode || !fieldIsEditable('omMeg')) && <p>{initialOmMeg}</p>}
          {isEditMode && fieldIsEditable('omMeg') && (
            <Textarea
              htmlId={'om-meg'}
              value={omMeg}
              onChange={(event) => setOmMeg(event.target.value)}
              rows={10}
              form={formId}
            />
          )}
        </Grid>
      </Grid>
    </>
  );
};

export default StudiestedUserprofile;
